import React, { useEffect, useMemo, useRef, useState } from "react";
import { FaEraser, FaFileExport, FaMagnifyingGlass } from "react-icons/fa6";
import Swal from "sweetalert2";
import "./activity-monitor.style.scss";
import "../../components/bill-list/bill-filter.style.scss";

// for date purpose
import moment from "moment";

// components
import ActivityItem from "../../components/activity-item/activity-item.component";
import SelectBox from "../../components/form-inputs/select-box.component";
import EmptyRecord from "../../components/empty-record/empty-record.component";
import FormError from "../../components/form-error/form-error.component";
import InlineNotice from "../../components/inline-notice/inline-notice.component";

// form handle
import { useFormik } from "formik";
import * as yup from "yup";

// global strings
import strings from "../../global/strings";

import { useSession } from "../../queries/auth.queries";

import { useActivityLog } from "../../queries/activity.queries";
import { useExportFile } from "../../queries/export.queries";

const activitySchema = yup.object().shape({
	year: yup.number().nullable().required("Year is required"),
	month: yup.number().nullable().required("Month is required"),
});

interface ActivityFilterValues {
	year: number;
	month: number;
}

const ITEMS_PER_PAGE = 10;

function currentFilterValues(): ActivityFilterValues {
	const now = new Date();
	return { year: now.getFullYear(), month: now.getMonth() };
}

function ActivityMonitor() {
	const { data: session } = useSession();

	// Filters actually applied to the query — only updated on Search submit /
	// Reset, so selecting a year/month doesn't refetch until submitted.
	const [appliedFilters, setAppliedFilters] =
		useState<ActivityFilterValues>(currentFilterValues());

	const exportMutation = useExportFile();

	const startMonth = moment({
		year: appliedFilters.year,
		month: appliedFilters.month,
		day: 1,
	}).format("YYYY-MM-DD");

	const { data, hasNextPage, isFetchingNextPage, fetchNextPage } =
		useActivityLog(startMonth, ITEMS_PER_PAGE);

	const activityLog = useMemo(
		() => data?.pages.flatMap((page) => page.rows) ?? [],
		[data],
	);

	// Infinite scroll off the page's own (body) scroll — no inner scroll
	// container, same sentinel + IntersectionObserver pattern as customer-list.page.tsx
	const sentinelRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const node = sentinelRef.current;
		if (!node) return;

		const observer = new IntersectionObserver(
			(entries) => {
				if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
					fetchNextPage();
				}
			},
			{ rootMargin: "200px" },
		);

		observer.observe(node);
		return () => observer.disconnect();
	}, [hasNextPage, isFetchingNextPage, fetchNextPage]);

	const filterFormik = useFormik<ActivityFilterValues>({
		validationSchema: activitySchema,
		initialValues: appliedFilters,
		onSubmit: (filterValues) => {
			setAppliedFilters({
				year: Number(filterValues.year),
				month: Number(filterValues.month),
			});
		},
		onReset: () => {
			setAppliedFilters(currentFilterValues());
		},
	});

	const handleActivityExport = async () => {
		Swal.fire({
			title: "Export Activity logs",
			text: "All your filtered activity logs will be exported to excel file. Are you sure?",
			icon: "info",
			showDenyButton: true,
			confirmButtonText: "Export",
			denyButtonText: "Cancel",
			customClass: {
				popup: "alert-modal",
				actions: "btn-container",
				confirmButton: "alert-btn btn-confirm",
				denyButton: "alert-btn btn-cancel",
			},
		}).then((result) => {
			if (result.isConfirmed && !exportMutation.isPending) {
				const activityFilter = {
					startMonth: moment({
						year: filterFormik.values?.year,
						month: filterFormik.values?.month,
						day: 1,
					}).format("YYYY-MM-DD"),
					endMonth: moment([
						filterFormik.values?.year,
						filterFormik.values?.month,
					])
						.endOf("month")
						.format("YYYY-MM-DD"),
				};
				exportMutation.mutate({
					tableName: "activity",
					filterValues: activityFilter,
					companyId: session?.company_id,
				});
			}
		});
	};

	return (
		<div className="activity-monitor-wrapper">
			<div className="activity-monitor-container">
				<div className="filter-container">
					<div className="filter-container-inner">
						<div className="filter-items-container">
							<h2 className="filter-section-header">
								Filter Activity Monitor
							</h2>
							<p className="action-description">
								The export to excel is based on filtered
								activity logs.
							</p>
							<div
								className="action-btn"
								aria-disabled={exportMutation.isPending}
								onClick={handleActivityExport}
							>
								<FaFileExport />
								Export to excel
							</div>
							<FormError
								isError={exportMutation.isError}
								error={exportMutation.error}
								fallback="Failed to export file."
							/>
							<InlineNotice
								message={exportMutation.isSuccess ? "File downloaded" : null}
								type="success"
								autoHideMs={3000}
								onDismiss={() => exportMutation.reset()}
							/>
						</div>
						<div className="filter-items-container">
							<div
								className="filter-row"
								style={{ flexWrap: "wrap" }}
							>
								<div className="input-item">
									<p className="input-label">Year</p>
									<SelectBox
										name="year"
										onChange={filterFormik.handleChange}
										onBlur={filterFormik.handleBlur}
										value={filterFormik.values?.year}
										options={strings.englishYear}
									/>
									{filterFormik.submitCount > 0 && (
										<div className="validation-error">
											{filterFormik?.errors?.year}
										</div>
									)}
								</div>
								<div className="input-item">
									<p className="input-label">Month</p>
									<SelectBox
										name="month"
										onChange={filterFormik.handleChange}
										onBlur={filterFormik.handleBlur}
										value={filterFormik.values?.month}
										options={strings.englishMonth}
									/>
									{filterFormik.submitCount > 0 && (
										<div className="validation-error">
											{filterFormik?.errors?.month}
										</div>
									)}
								</div>
							</div>
						</div>
						<div className="filter-btn-container">
							<div
								className="filter-btn inverted"
								onClick={() => filterFormik.resetForm()}
							>
								<FaEraser />Reset
							</div>
							<div
								className="filter-btn"
								onClick={filterFormik.submitForm}
							>
								<FaMagnifyingGlass />
								Search
							</div>
						</div>
					</div>
				</div>
				<div className="activities-list-wrapper">
					<div className="section-header-container">
						<h2 className="section-header-title">
							Activity Monitor
						</h2>
					</div>
					<div className="activities-container">
						{Array.isArray(activityLog) &&
						activityLog.length > 0 ? (
							<>
								{activityLog.map((activity, index) => (
									<ActivityItem
										key={activity.activity_log_id ?? index}
										activity={activity}
									/>
								))}
								<div ref={sentinelRef} />
							</>
						) : (
							<EmptyRecord />
						)}
					</div>
				</div>
			</div>
		</div>
	);
}

export default ActivityMonitor;
